import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { EmptyState, ErrorState, LoadingSkeleton, LoadingState } from "@/components/states";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { generateFlashcards, reviewFlashcard } from "@/lib/study.functions";
import { Sparkles, Loader2, ArrowLeft, ArrowRight, RotateCw, Layers } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { RichText } from "@/components/AssistantMessage";
import { MediaAttachButton } from "@/components/MediaAttachButton";

export const Route = createFileRoute("/_app/flashcards")({
  head: () => ({
    meta: [
      { title: "Flashcard — Lumen" },
      { name: "description", content: "Ripasso spaziato con flashcard generate dall'IA. Sistema Leitner a 5 box." },
    ],
  }),
  component: FlashcardsPage,
});

type Deck = { id: string; title: string; subject_name: string | null; created_at: string };
type Flashcard = { id: string; front: string; back: string; box: number; next_review_at: string; deck_id: string };

function FlashcardsPage() {
  const { t } = useTranslation();
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);
  const [tab, setTab] = useState("review");
  return (
    <div className="space-y-5 px-5 pt-8">
      <PageHeader
        tone="sage"
        icon={<Layers className="h-5 w-5" />}
        kicker={t("flashcards.eyebrow")}
        title={t("flashcards.title")}
        subtitle={t("flashcards.subtitle")}
      />
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v !== "review") setActiveDeckId(null); }}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="review">{t("flashcards.tabReview")}</TabsTrigger>
          <TabsTrigger value="decks">{t("flashcards.tabDecks")}</TabsTrigger>
          <TabsTrigger value="create">{t("flashcards.tabCreate")}</TabsTrigger>
        </TabsList>
        <TabsContent value="review" className="mt-4">
          <ReviewPanel key={activeDeckId ?? "due"} deckId={activeDeckId} onClearDeck={() => setActiveDeckId(null)} />
        </TabsContent>
        <TabsContent value="decks" className="mt-4">
          <DecksPanel onOpen={(id) => { setActiveDeckId(id); setTab("review"); }} />
        </TabsContent>
        <TabsContent value="create" className="mt-4"><CreatePanel /></TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------------- REVIEW --------------------------------- */

function ReviewPanel({ deckId, onClearDeck }: { deckId: string | null; onClearDeck: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: deckInfo } = useQuery({
    queryKey: ["deck-info", deckId],
    enabled: !!deckId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("flashcard_decks")
        .select("id,title")
        .eq("id", deckId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const cardsQ = useQuery({
    queryKey: ["flashcards", deckId ?? "due"],
    queryFn: async () => {
      let q = supabase
        .from("flashcards")
        .select("id,front,back,box,next_review_at,deck_id");
      if (deckId) {
        q = q.eq("deck_id", deckId).order("created_at", { ascending: true });
      } else {
        q = q
          .lte("next_review_at", new Date().toISOString())
          .order("next_review_at", { ascending: true })
          .limit(30);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Flashcard[];
    },
  });

  const cards = cardsQ.data ?? [];
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const runReview = useServerFn(reviewFlashcard);

  useEffect(() => {
    setIdx(0);
    setFlipped(false);
  }, [deckId]);

  const safeIdx = idx >= cards.length ? 0 : idx;
  const card = cards[safeIdx];

  const rate = useMutation({
    mutationFn: async (quality: "again" | "hard" | "good") => {
      if (!card) return;
      await runReview({ data: { id: card.id, quality } });
    },
    onSuccess: () => {
      setFlipped(false);
      if (idx + 1 >= cards.length) {
        toast.success(t("flashcards.reviewDoneToast"));
        qc.invalidateQueries({ queryKey: ["flashcards"] });
        setIdx(0);
      } else {
        setIdx((i) => i + 1);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (cardsQ.isPending) return <LoadingState label={t("flashcards.loading")} />;
  if (cardsQ.isError) return <ErrorState error={cardsQ.error} onRetry={() => cardsQ.refetch()} />;
  if (cards.length === 0) {
    return (
      <Card className="card-soft border-0 p-6 text-center">
        <p className="text-sm">{deckId ? t("flashcards.deckEmpty") : t("flashcards.nothingToReview")}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {deckId ? t("flashcards.backToDecksHint") : t("flashcards.generateOrComeBack")}
        </p>
        {deckId && (
          <Button variant="outline" className="mt-3" onClick={onClearDeck}>{t("flashcards.backToReview")}</Button>
        )}
      </Card>
    );
  }
  if (!card) return null;

  return (
    <div className="space-y-4">
      {deckId && deckInfo && (
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="max-w-[70%] truncate">📚 {deckInfo.title}</Badge>
          <Button variant="ghost" size="sm" onClick={onClearDeck}>{t("flashcards.allDecks")}</Button>
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t("flashcards.cardOf", { current: safeIdx + 1, total: cards.length })}</span>
        <Badge variant="secondary">{t("flashcards.boxOf", { box: card.box })}</Badge>
      </div>

      <Card
        onClick={() => setFlipped((f) => !f)}
        className="card-soft flex min-h-64 cursor-pointer flex-col items-center justify-center border-0 p-6 text-center"
      >
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {flipped ? t("flashcards.answer") : t("flashcards.question")}
        </p>
        <RichText content={flipped ? card.back : card.front} className="mt-3 block text-lg font-medium leading-relaxed" />
        <p className="mt-6 flex items-center gap-1 text-xs text-muted-foreground">
          <RotateCw className="h-3 w-3" /> {t("flashcards.tapToFlip")}
        </p>
      </Card>

      {flipped ? (
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" onClick={() => rate.mutate("again")} disabled={rate.isPending}>
            {t("flashcards.rateAgain")}
          </Button>
          <Button variant="secondary" onClick={() => rate.mutate("hard")} disabled={rate.isPending}>
            {t("flashcards.rateHard")}
          </Button>
          <Button onClick={() => rate.mutate("good")} disabled={rate.isPending}>
            {t("flashcards.rateGood")}
          </Button>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setFlipped(true)} className="w-full">
          {t("flashcards.showAnswer")}
        </Button>
      )}
    </div>
  );
}

/* --------------------------------- DECKS ---------------------------------- */

function DecksPanel({ onOpen }: { onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const decksQ = useQuery({
    queryKey: ["decks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("flashcard_decks")
        .select("id,title,subject_name,created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Deck[];
    },
  });

  const decks = decksQ.data ?? [];
  if (decksQ.isPending) return <LoadingSkeleton rows={3} />;
  if (decksQ.isError) return <ErrorState error={decksQ.error} onRetry={() => decksQ.refetch()} />;
  if (decks.length === 0) return <EmptyState description={t("flashcards.emptyDecks")} />;

  return (
    <div className="space-y-2">
      {decks.map((d) => (
        <Card
          key={d.id}
          onClick={() => onOpen(d.id)}
          className="card-soft flex cursor-pointer items-center gap-3 border-0 p-4 transition hover:bg-accent/50 active:scale-[0.99]"
        >
          <div className="rounded-xl bg-primary/10 p-2 text-primary"><Layers className="h-4 w-4" /></div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{d.title}</p>
            {d.subject_name && <p className="text-xs text-muted-foreground">{d.subject_name}</p>}
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Card>
      ))}
    </div>
  );
}

/* --------------------------------- CREATE --------------------------------- */

function CreatePanel() {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [source, setSource] = useState("");
  const [subject, setSubject] = useState("");
  const [count, setCount] = useState(10);
  const [attachedName, setAttachedName] = useState<string | null>(null);
  const qc = useQueryClient();
  const runGen = useServerFn(generateFlashcards);

  const create = useMutation({
    mutationFn: async () =>
      runGen({ data: { topic, sourceText: source || undefined, subject: subject || undefined, count } }),
    onSuccess: (r) => {
      toast.success(t("flashcards.createdToast", { title: r.title, count: r.count }));
      setTopic(""); setSource("");
      qc.invalidateQueries({ queryKey: ["decks"] });
      qc.invalidateQueries({ queryKey: ["flashcards"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="card-soft space-y-3 border-0 p-4">
      <div className="space-y-1.5">
        <Label htmlFor="fc-topic">{t("flashcards.topicLabel")}</Label>
        <Input id="fc-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("flashcards.topicPlaceholder")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="fc-subject">{t("flashcards.subjectLabel")}</Label>
        <Input id="fc-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("flashcards.subjectPlaceholder")} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="fc-source">{t("flashcards.sourceLabel")}</Label>
        <Textarea
          id="fc-source"
          value={source}
          onChange={(e) => { setSource(e.target.value); setAttachedName(null); }}
          rows={4}
          placeholder={t("flashcards.sourcePlaceholder")}
        />
        <MediaAttachButton
          attachedName={attachedName}
          onClear={() => { setAttachedName(null); setSource(""); }}
          onText={(text, name) => {
            setSource(text);
            setAttachedName(name);
            if (!topic.trim()) setTopic(name.replace(/\.[^.]+$/, "").slice(0, 60));
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label>{t("flashcards.countLabel", { count })}</Label>
        <input type="range" min={4} max={20} value={count} onChange={(e) => setCount(Number(e.target.value))} className="w-full" />
      </div>
      <Button
        onClick={() => create.mutate()}
        disabled={create.isPending || topic.trim().length < 3}
        className="w-full rounded-xl"
      >
        {create.isPending ? (
          <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("flashcards.generating")}</>
        ) : (
          <><Sparkles className="mr-1.5 h-4 w-4" /> {t("flashcards.generateWithAi")}</>
        )}
      </Button>
    </Card>
  );
}

// Suppress unused-import lint for icons
void ArrowLeft;
